import type { EnergyBidSnapshot } from '@/features/bidding/types/bidDetail/EnergyBidSnapshot'
import type { CapacityObligation } from '@/features/bidding/types/capacityObligations'
import { sumPtuOfferedWatts } from '@/features/bidding/utils/bidDetail/sumPtuOfferedWatts'

export type PtuRollup = Readonly<{
  offeredWatts: number
  obligationWatts: number
}>

/**
 * Per-PTU rollup of `offeredWatts` / `obligationWatts`, keyed by
 * `ptu.start.toMillis()`. Responsible for keeping all inputs on the same
 * AG scope so the sums are comparable.
 */
export function computePtuObligationVsOffered(
  snapshots: ReadonlyArray<EnergyBidSnapshot>,
  obligations: ReadonlyArray<CapacityObligation>,
  ptuLengthInMinutes: number,
): Map<number, PtuRollup> {
  const rollup = new Map<number, PtuRollup>()
  const add = (key: number, field: keyof PtuRollup, value: number) => {
    const prev = rollup.get(key) ?? { offeredWatts: 0, obligationWatts: 0 }
    rollup.set(key, { ...prev, [field]: prev[field] + value })
  }

  for (const snapshot of snapshots) {
    for (const ptuSnapshot of snapshot.ptus) {
      add(ptuSnapshot.ptu.start.toMillis(), 'offeredWatts', sumPtuOfferedWatts(ptuSnapshot))
    }
  }
  for (const obligation of obligations) {
    for (const ptu of obligation.ptus) {
      // Capacity obligation is at 1 hour granularity. Explode that to match Energy 15-min PTU
      for (let slot = ptu.startAt; slot < ptu.endAt; slot = slot.plus({ minutes: ptuLengthInMinutes })) {
        add(slot.toMillis(), 'obligationWatts', ptu.volume.quantity)
      }
    }
  }

  return rollup
}
